import { env } from './settings';
import { elasticLogEvent, elasticLogError } from './ElasticLog';
import { getRobotId } from '../vodjs/lib';
import { areSame } from './Utils';
import { clearFetch, allowMulti } from './Utils';
import { StepStatus } from './StepStatus';
import { debugLog, getLockProxyHost } from '../constants/Meta';
import { getConfig } from '../utils/configUtils';
import { taskSubscribe } from './TrackedTasks';


const twitchFuncSecs = 10;
let registrations = {};
let prevTimestamp = 0;
let pollStarted = false;
let pollAllSubscribers = [];
let stepStatusSubscribers = [];
let tubeSubscribers = [];
let socialCueSubscribers = [];
let estopSubscribers = [];
let nextSubscriberId = 1;
let prevTubesValue;
let prevStepStatus;
let prevTubes;
let tasks = [];
let tasksUnsubscribe;

let defaultNextCallTicks = 1000;
let pollSpeedResetTimeout = undefined;

export function tmpIncreasePollSpeed() {
  if (pollSpeedResetTimeout) {
    clearTimeout(pollSpeedResetTimeout);
    pollSpeedResetTimeout = undefined;
  }

  defaultNextCallTicks = 150;

  pollSpeedResetTimeout = setTimeout(() => {
    defaultNextCallTicks = 1000;
    pollSpeedResetTimeout = undefined;
  }, 1000 * 3);
}

export function PollMgrStartPolling() {
  if (pollStarted) return;
  pollStarted = true;
  setTimeout(pollMost, 1000);

  tasksUnsubscribe = tasksUnsubscribe || taskSubscribe(tasksArg => tasks = tasksArg || []);
}

export function PollMgrStopPolling() {
  pollStarted = false;
  if(tasksUnsubscribe) {
    tasksUnsubscribe();
    tasksUnsubscribe = undefined;
  }
}

export function PollMgrRegister(key, startFunc) {
  if (registrations[key]) return; //already registered

  let stopFunc = startFunc();
  registrations[key] = { key, startFunc, stopFunc };
}

export function PollMgrUnregister(key) {
  if (!registrations[key]) {
    console.log('PollMgrUnregister called with unvalued key');
    return;
  }

  let rec = registrations[key];
  delete registrations[key];
  rec.stopFunc();
}

let pollStats = { startTicks: Date.now(), count: 0, total: 0, prevGap: 0 };
let prevPollSuccess = true;
let badPollStartTicks = undefined;

async function pollMost() {
  let start = Date.now();

  let {isSim} = env.firestore;
  let nonLocal = getConfig().nonLocal;
  const isLocalSim = isSim && !nonLocal;
  try {
    let options = {
      method: isLocalSim ? 'PUT' : 'GET',
      headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
      body: isLocalSim ? JSON.stringify({ robotId: getRobotId() }) : undefined,
    };

    let result;
    const mostUrl = getLockProxyHost() + '/most';
    await clearFetch(mostUrl, options, {
      success: (json) => {
        if(!pollStarted) return;

        result = json;

        let gap = Date.now() - start;

        if (!prevPollSuccess) {
          let spanTicks = badPollStartTicks ? Date.now() - badPollStartTicks : undefined;
          badPollStartTicks = undefined;
          if (allowMulti('pollsGood', 10, 10)) elasticLogEvent({ what: 'pollsGood', spanTicks });
        }
        prevPollSuccess = true;

        if (gap > 200 && gap > pollStats.prevGap * 1.5) {
          if (allowMulti('pollMostGap', 2, 5)) elasticLogEvent({ what: 'pollMostGap', gap });
        }

        pollStats.prevGap = gap;

        pollStats.total += gap;
        pollStats.count += 1;
        if (Date.now() - pollStats.startTicks >= 1000 * 60 * 60) {
          elasticLogEvent({ what: 'pollMostStats', ...pollStats });
          pollStats = { startTicks: Date.now(), count: 0, total: 0, prevGap: 0 };
        }
      },
      bad: ({ logStr }) => {
        if (prevPollSuccess) {
          badPollStartTicks = Date.now();
          if (allowMulti('pollsBad', 10, 10)) elasticLogEvent({ what: 'pollsBad', logStr, isSim, nonLocal, mostUrl });
        }
        prevPollSuccess = false;

        if (logStr.indexOf('Network request failed') < 0 && allowMulti('pollMostErr', 10, 10))
          elasticLogError({ what: 'pollMostErr', msg: logStr });
      },
    });

    if(!pollStarted) return;

    if (result) {
      let { net_status, tubes, step_status } = result;
      const socialCue = tubes.social_cue;

      try {
        logLockWrites(step_status, tubes);
      }
      catch (err) {
        if (allowMulti('logLockWritesCatch', 20, 60))
          elasticLogError({ what: 'logLockWritesCatch', msg: 'logLockWritesCatch: ' + err });
      }


      try {
        logTaskAbsence(step_status);
      }
      catch (err) {
        if (allowMulti('logTaskAbsenceCatch', 20, 60))
          elasticLogError({ what: 'logTaskAbsenceCatch', msg: 'logTaskAbsenceCatch: ' + err });
      }

      try {
        handleAll(result);
      }
      catch(err) {
        debugLog('pollAllCatch: ' + err);
        if (allowMulti('pollAllCatch', 20, 60))
          elasticLogError({ what: 'pollAllCatch', msg: 'pollAllCatch: ' + err, result: JSON.stringify(result), stack: err.stack });
      }


      try {
        handleStepStatus(step_status);
      }
      catch (err) {
        debugLog('stepStatCatch: ' + err);
        if (allowMulti('stepStatCatch', 20, 60))
          elasticLogError({ what: 'stepStatCatch', msg: 'stepStatCatch: ' + err, step_status: JSON.stringify(step_status), stack: err.stack });
      }

      try {
        handleNetStatus(net_status);
      }
      catch (err) {
        if (allowMulti('netStatCatch', 20, 60)) elasticLogError({ what: 'netStatCatch', msg: 'netStatCatch: ' + err });
      }

      try {
        handleTubes(tubes);
      }
      catch (err) {
        if (allowMulti('handleTubesCatch', 20, 60)) elasticLogError({ what: 'tubesCatch', msg: 'tubesCatch: ' + err });
      }

      try {
        handleSocialCue(socialCue);
      }
      catch (err) {
        if (allowMulti('handleSocialCueCatch', 20, 60))
          elasticLogError({ what: 'socialCueCatch', msg: 'socialCueCatch: ' + err });
      }

      try {
        handleEstop(tubes?.aggregated_estop_state);
      }
      catch (err) {
        if (allowMulti('handleEstopCatch', 20, 60))
          elasticLogError({ what: 'handleEstopCatch', msg: 'handleEstopCatch: ' + err });
      }
    }
  }
  catch (err) {
    debugLog('pollMostCatch: ' + err);
    if (allowMulti('pollMostCatch', 10, 60)) elasticLogError({ what: 'pollMostCatch', msg: 'pollMostCatch: ' + err });
  }
  finally {
    if(!pollStarted) return;
    let nextCallTicks = Math.max(0, defaultNextCallTicks - (Date.now() - start));
    setTimeout(pollMost, nextCallTicks);
  }
}


//only makes sense when called on a robot's odweb
export async function onBotTaskCancel(taskId) {
  let start = Date.now();
  let url = getLockProxyHost() + '/cancel_task';
  let bodyObj = { task_id:taskId };
  let method = 'POST';
  try {
    elasticLogEvent({ what: 'markStaleAttempt', msg:`attempting to mark task stale: ${taskId}`, taskId });

    let options = {
      method,
      headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
      body: JSON.stringify(bodyObj)
    };

    await clearFetch(url, options, {
      success: (json) => elasticLogEvent({ what: 'staleSuccess', msg:`success marking stale: ${taskId}`, taskId }),
      bad: ({ logStr }) => elasticLogError({ what: 'staleErr', msg: `onBotTaskCancel: ${taskId}: ` + logStr, taskId, url, method, body:bodyObj})
    });
  }
  catch(err) {
    elasticLogError({ what: 'staleErr2', msg: `onBotTaskCancel2: ${taskId}: ` + err, taskId, url, method, body:bodyObj})
  }
}


function restartSubscriptions() {
  let start = Date.now();
  Object.values(registrations).forEach((reg) => {
    reg.stopFunc();
    registrations[reg.key] = { ...reg, stopFunc: reg.startFunc() };
  });
  console.log('restartSubscriptions took ' + Math.floor((Date.now() - start) / 100) / 10 + ' seconds.');
}

function handleNetStatus(net_status) {
  if (!net_status) return;
  if (
    prevTimestamp && //if this isn't simply the first incoming data
    net_status.timestamp > prevTimestamp //and timestamp has changed
  ) {
    if (net_status.current_type)
      //if we have network
      restartSubscriptions();

    elasticLogEvent({ what: 'netstatus', data: net_status });
  }
  prevTimestamp = net_status.timestamp;
}

export function subscribePollAll(cb) {
  let id = nextSubscriberId++;
  pollAllSubscribers.push({ id, cb });
  return () => (pollAllSubscribers = pollAllSubscribers.filter((subscriber) => subscriber.id != id));
}

function handleAll(pollAll) {
  pollAllSubscribers.forEach((subscriber) => subscriber.cb(pollAll));
}



export function subscribeStepStatus(cb) {
  let id = nextSubscriberId++;
  stepStatusSubscribers.push({ id, cb });
  return () => (stepStatusSubscribers = stepStatusSubscribers.filter((subscriber) => subscriber.id != id));
}


function handleStepStatus(step_status) {
  //step_status can potentially be undefined in UI-sim, potentially in ros-sim
  let stepStatusObj = step_status ? new StepStatus(step_status) : undefined;
  stepStatusSubscribers.forEach((subscriber) => subscriber.cb(stepStatusObj));
  prevStepStatus = step_status;
}

export function getCurStepStatus() {
  return prevStepStatus;
}


export function subscribeTubes(cb) {
  let id = nextSubscriberId++;
  tubeSubscribers.push({ id, cb });

  //send initial value
  if(prevTubesValue) {
    try {
      cb(prevTubesValue);
    }
    catch(err) {
      elasticLogError({ what: 'tubesInit', msg: `subscribeTubes: initial err: ` + err });
    }
  }

  return () => (tubeSubscribers = tubeSubscribers.filter((subscriber) => subscriber.id != id));
}


function handleTubes(tubes) {
  if (!tubes) return;

  //added 250407, after jiggering when pollMgr feeds this handler
  let same = areSame({...prevTubesValue, robot_time:undefined}, {...tubes, robot_time:undefined});
  if(same) return;  

  tubeSubscribers.forEach((subscriber) => subscriber.cb(tubes));
  prevTubesValue = tubes;
}

export function subscribeSocialCue(cb) {
  let id = nextSubscriberId++;
  socialCueSubscribers.push({ id, cb });
  return () => (socialCueSubscribers = socialCueSubscribers.filter((subscriber) => subscriber.id != id));
}

export function subscribeEstop(cb) {
  let id = nextSubscriberId++;
  estopSubscribers.push({ id, cb });
  return () => (estopSubscribers = estopSubscribers.filter((subscriber) => subscriber.id != id));
}

function handleSocialCue(socialCue) {
  if (socialCue === undefined) {
    // Expected on versions of moxi_dev before LVTRS-589
    return;
  }
  socialCueSubscribers.forEach((subscriber) => subscriber.cb(socialCue));
}

function handleEstop(aggregated_estop_state) {
  estopSubscribers.forEach((subscriber) => subscriber.cb(aggregated_estop_state));
}


//log when we're on our way to a pickup or dropoff and don't have the relevant task record
function logTaskAbsence(step_status) {
  if(!step_status) return;
  let {step_type, task_uuid} = step_status;
  if(!step_type || !task_uuid) return;
  if(!['goto', 'secure_goto'].includes(step_type)) return;

  let task = tasks.find(t => t.uuid == task_uuid);
  if(task) return;

  if(allowMulti('missingTask', 1, 0.5)) {
    let msg = `Task record absent for ${task_uuid.slice(0, 6)} during ${step_type}`;
    elasticLogEvent({ what: 'pollTaskAbsent', msg, step_type, task_uuid });
  }

}

function logLockWrites(step_status, tubes) {
  if (!tubes) return;

  let step_type = step_status ? step_status.step_type : undefined;
  if (step_type == 'load_tube' || step_type == 'unload_tube') {
    //during load/unload we have other logging
    prevTubes = undefined;
  }
  else {
    if (prevTubes) {
      let diffs = [];

      ['small', 'medium', 'large'].forEach((d) => {
        let drawerDiffs = [];

        let a = prevTubes[d] || {},
          b = tubes[d] || {};
        if (a.access_status != b.access_status) drawerDiffs.push(`status=${b.access_status || '-'}`);
        if (a.destination != b.destination) drawerDiffs.push(`dst=${b.destination || '-'}`);
        if (a.filled != b.filled) drawerDiffs.push(`filled=${b.filled}`);
        if (a.unlocked != b.unlocked) drawerDiffs.push(`unlocked=${b.unlocked}`);

        if (drawerDiffs.length) {
          diffs.push(`${d}: ${drawerDiffs.join(', ')}`);
        }
      });
      if (diffs.length) elasticLogEvent({ what: 'lockWrite', diffs: diffs.join('; ') });
    }
    prevTubes = tubes;
  }
}

export async function generatePinCodes(tasks) {
  let options = {
    method: 'POST',
    headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
    body: env.firestore.isSim ? JSON.stringify({ robotId: getRobotId(), ids: tasks }) : JSON.stringify({ ids: tasks }),
  };

  let result = {};
  await clearFetch(getLockProxyHost() + '/request_pin', options, {
    success: (json) => {
      result = json;
    },
    bad: ({ logStr }) => {
      elasticLogEvent({ what: 'requestPinFailed', logStr });
    },
  });

  return result;
}

export async function validatePinCode(code, taskId, isAdminCode = false) {
  const data = {
    id: taskId,
    code,
    admin: isAdminCode,
  };
  let options = {
    method: 'POST',
    headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
    body: env.firestore.isSim ? JSON.stringify({ ...data, robotId: getRobotId() }) : JSON.stringify(data),
  };

  let result = {};
  await clearFetch(getLockProxyHost() + '/validate_pin', options, {
    success: (json) => {
      result = json;
    },
    bad: ({ logStr }) => {
      elasticLogEvent({ what: 'validatePinFailed', logStr });
    },
  });

  return result;
}

export async function setAdminOverride(value, taskId) {
  const data = {
    task_id: taskId,
    requested: value,
  };
  let options = {
    method: 'POST',
    headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
    body: env.firestore.isSim ? JSON.stringify({ ...data, robotId: getRobotId() }) : JSON.stringify(data),
  };

  let result = {};
  await clearFetch(getLockProxyHost() + '/admin_override', options, {
    success: (json) => {
      result = json;
    },
    bad: ({ logStr }) => {
      elasticLogEvent({ what: 'setAdminOverrideFailed', logStr });
    },
  });

  return result;
}

export async function adminOpenDrawers(taskIds) {
  const data = {
    task_ids: taskIds,
  };
  let options = {
    method: 'POST',
    headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
    body: env.firestore.isSim ? JSON.stringify({ ...data, robotId: getRobotId() }) : JSON.stringify(data),
  };

  let result = null;
  await clearFetch(getLockProxyHost() + '/admin_open_drawers', options, {
    success: (json) => {
      result = json;
    },
    bad: ({ logStr }) => {
      elasticLogEvent({ what: 'adminOpenDrawersFailed', logStr });
    },
  });

  return result;
}
